import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  Users,
  Settings,
  Wallet,
  Zap,
  ArrowLeftRight,
  FileText,
  Bell,
  User,
  LogOut,
  Menu as MenuIcon,
  X,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
}

const MENU_ITEMS: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/admin/dashboard' },
  { id: 'accounts', label: 'Contas', icon: Wallet, path: '/admin/accounts' },
  { id: 'transactions', label: 'Transações', icon: ArrowLeftRight, path: '/admin/transactions' },
  { id: 'pix', label: 'PIX', icon: Zap, path: '/admin/pix' },
  { id: 'users', label: 'Usuários', icon: Users, path: '/admin/users' },
  { id: 'reports', label: 'Relatórios', icon: FileText, path: '/admin/reports' },
  { id: 'settings', label: 'Configurações', icon: Settings, path: '/admin/settings' },
];

export function MinimalLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-surface-950">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="grid-bg absolute inset-0 opacity-30" />
      </div>

      {/* Minimal Header */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-surface-900/95 backdrop-blur-xl border-b border-surface-800 z-40">
        <div className="h-full px-4 flex items-center justify-between">
          {/* Left Side */}
          <div className="flex items-center gap-4">
            {/* Hamburger Menu */}
            <button
              onClick={() => setIsDrawerOpen(true)}
              className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
            >
              <MenuIcon className="w-5 h-5" />
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-xs">U</span>
              </div>
              <span className="text-white font-semibold text-sm hidden sm:block">UniãoBR</span>
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Notifications */}
            <button className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors relative">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>

            {/* User */}
            <Link
              to="/profile"
              className="flex items-center gap-2 p-2 text-surface-300 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-primary-500/20 flex items-center justify-center">
                <User className="w-4 h-4 text-primary-400" />
              </div>
            </Link>
          </div>
        </div>
      </header>

      {/* Drawer Overlay */}
      <AnimatePresence>
        {isDrawerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={() => setIsDrawerOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Drawer */}
      <AnimatePresence>
        {isDrawerOpen && (
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 left-0 bottom-0 w-72 bg-surface-900 border-r border-surface-800 z-50 flex flex-col"
          >
            {/* Drawer Header */}
            <div className="h-14 px-4 flex items-center justify-between border-b border-surface-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">U</span>
                </div>
                <span className="text-white font-semibold">UniãoBR</span>
              </div>
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="p-2 text-surface-400 hover:text-white hover:bg-surface-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-surface-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary-400" />
                </div>
                <div>
                  <div className="text-white font-medium text-sm">{user?.name || 'Usuário'}</div>
                  <div className="text-surface-400 text-xs">{user?.email || 'email@exemplo.com'}</div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
              {MENU_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={() => setIsDrawerOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-lg transition-colors',
                      isActive
                        ? 'bg-primary-500/20 text-primary-400'
                        : 'text-surface-300 hover:bg-surface-800 hover:text-white'
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                );
              })}
            </nav>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-surface-800">
              <button
                onClick={() => {
                  logout();
                  setIsDrawerOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-3 w-full text-red-400 hover:bg-surface-800 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Sair</span>
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="pt-14 min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="p-4 md:p-6"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}

export default MinimalLayout;
